import * as React from "react";

// MUI
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Snackbar from "@mui/material/Snackbar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Third Party
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useLazyQuery } from "@apollo/client";

// Internal
import Logo from "../../../assets/logo-primary.png";
import en from "../../../data/en.json";
import { UPSERT_USER } from "../../../data/graphql/mutations";
import { GET_USER_PROFILE_BY_ID } from "../../../data/graphql/queries";

const rightLink = {
  fontSize: 16,
  color: "primary.main",
  mx: 1.5, // Base margin
};

const rightLinkWide = {
  fontSize: 16,
  color: "primary.main",
  mx: 2.5, // Wider margin for items that need more space
};

const rightLinkExtraWide = {
  fontSize: 16,
  color: "primary.main",
  mx: 3.5, // Wider margin for items that need more space
};

const rightLinkNarrow = {
  fontSize: 16,
  color: "primary.main",
  mx: 1, // Narrower margin for items that are too far apart
};

function AppAppBar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [upsertUser, { error: UpsertUserError, data: UpsertUserData }] =
    useMutation(UPSERT_USER);

  const [getUserProfile, { data: userProfileData }] = useLazyQuery(GET_USER_PROFILE_BY_ID);

  const { loginWithRedirect, user } = useAuth0();
  const { logout } = useAuth0();
  const { isAuthenticated, isLoading } = useAuth0();

  const isAdmin = userProfileData?.userProfileById?.role === "admin";

  const [snackbarMessage, setSnackbarMessage] = React.useState<null | string>(
    null
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [servicesAnchorEl, setServicesAnchorEl] = React.useState(null);
  const [publishingAnchorEl, setPublishingAnchorEl] = React.useState(null);
  const [authAnchorEl, setAuthAnchorEl] = React.useState(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = React.useState(null);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleServicesClick = (event: any) => {
    setServicesAnchorEl(event.currentTarget);
  };

  const handleServicesClose = () => {
    setServicesAnchorEl(null);
  };

  const handlePublishingClick = (event: any) => {
    setPublishingAnchorEl(event.currentTarget);
  };

  const handlePublishingClose = () => {
    setPublishingAnchorEl(null);
  };

  const handleAuthClick = (event: any) => {
    setAuthAnchorEl(event.currentTarget);
  };

  const handleAuthClose = () => {
    setAuthAnchorEl(null);
  };

  const handleMobileMenuOpen = (event: any) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  React.useEffect(() => {
    if (isAuthenticated) {
      upsertUser({
        variables: {
          email: user?.email,
          auth0_id: user?.sub,
          role: "member",
          name: user?.name,
          picture_url: user?.picture,
        },
      });
      getUserProfile({ variables: { user_auth0_id: user?.sub } });
    }
  }, [isAuthenticated, user, upsertUser, getUserProfile]);

  React.useEffect(() => {
    if (UpsertUserData !== undefined) {
      setSnackbarMessage(en.common.loginSuccess);
    }
    if (UpsertUserError !== undefined) {
      setSnackbarMessage(en.common.loginFail);
    }
  }, [UpsertUserData, UpsertUserError]);

  if (isLoading) {
    return (
      <AppBar position="fixed" color="primary">
        <Toolbar sx={{ justifyContent: "center" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Box component="img" sx={{ height: 24 }} alt="logo" src={Logo} />
            <Link
              variant="h6"
              underline="none"
              href="/"
              sx={{ pl: 2, fontSize: 24, color: "secondary.light" }}
            >
              {en.common.inkBowl}
            </Link>
          </Box>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <React.Fragment>
      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={3000}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
      />
      <AppBar position="fixed" color="primary">
        <Toolbar sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              minWidth: { xs: 200, md: 250 },
            }}
          >
            <Box component="img" sx={{ height: 24 }} alt="logo" src={Logo} />
            <Link
              variant="h6"
              underline="none"
              href="/"
              sx={{
                pl: 2,
                fontSize: { xs: 20, md: 24 },
                color: "secondary.light",
              }}
            >
              {en.common.inkBowl}
            </Link>
            {isAdmin && (
              <Button
                onClick={() => window.open("/admin")}
                sx={{
                  ml: 2,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                  textTransform: "uppercase",
                  fontSize: 14,
                }}
              >
                Admin
              </Button>
            )}
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                minWidth: 650,
              }}
            >
              <Button
                onClick={() => window.open("/about")}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                }}
              >
                {en.marketing.about}
              </Button>

              <Button
                aria-controls="services-menu"
                aria-haspopup="true"
                onClick={handleServicesClick}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                  minHeight: "auto",
                  textTransform: "uppercase",
                  alignSelf: "center",
                }}
              >
                {en.marketing.features}
              </Button>
              <Menu
                id="services-menu"
                anchorEl={servicesAnchorEl}
                keepMounted
                open={Boolean(servicesAnchorEl)}
                onClose={handleServicesClose}
                sx={{ ...rightLinkNarrow }}
              >
                <MenuItem
                  onClick={() => {
                    handleServicesClose();
                    window.location.href = "/features#essaycraft";
                  }}
                >
                  essaycraft ai
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleServicesClose();
                    window.location.href = "/features#inkbowl-publishing";
                  }}
                >
                  inkbowl publishing
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleServicesClose();
                    window.location.href = "/features#inklink";
                  }}
                >
                  inklink network
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleServicesClose();
                    window.location.href = "/features#versecanvas";
                  }}
                >
                  versecanvas ai
                </MenuItem>
              </Menu>

              <Button
                aria-controls="publishing-menu"
                aria-haspopup="true"
                onClick={handlePublishingClick}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                  minHeight: "auto",
                  textTransform: "uppercase",
                  alignSelf: "center",
                }}
              >
                {en.common.publishing}
              </Button>
              <Menu
                id="publishing-menu"
                anchorEl={publishingAnchorEl}
                keepMounted
                open={Boolean(publishingAnchorEl)}
                onClose={handlePublishingClose}
                sx={{ ...rightLinkNarrow }}
              >
                <MenuItem
                  onClick={() => {
                    handlePublishingClose();
                    window.open("/digital-archive");
                  }}
                >
                  {en.marketing.publicationArchive}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handlePublishingClose();
                    window.open("/publishing");
                  }}
                >
                  {en.marketing.about}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handlePublishingClose();
                    window.open("/submissions");
                  }}
                >
                  submissions
                </MenuItem>
              </Menu>

              <Button
                onClick={() => window.open("/community")}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                }}
              >
                {en.marketing.inkLink}
              </Button>

              <Button
                aria-controls="more-menu"
                aria-haspopup="true"
                onClick={handleClick}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                  minHeight: "auto",
                  textTransform: "uppercase",
                  alignSelf: "center",
                }}
              >
                {en.marketing.learnMore}
              </Button>
              <Menu
                id="more-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{ ...rightLinkNarrow }}
              >
                <MenuItem onClick={() => { handleClose(); window.open("/guidelines"); }}>
                  {en.marketing.communityGuideline}
                </MenuItem>
                <MenuItem onClick={() => { handleClose(); window.open("/publications"); }}>
                  {en.common.publications}
                </MenuItem>
              </Menu>

              {isAuthenticated && (
                <>
                  <Button
                    aria-controls="auth-menu"
                    aria-haspopup="true"
                    onClick={handleAuthClick}
                    sx={{
                      ...rightLink,
                      color: "secondary.light",
                      whiteSpace: "nowrap",
                      minHeight: "auto",
                      textTransform: "uppercase",
                      alignSelf: "center",
                    }}
                  >
                    {en.common.dashboard}
                  </Button>
                  <Menu
                    id="auth-menu"
                    anchorEl={authAnchorEl}
                    keepMounted
                    open={Boolean(authAnchorEl)}
                    onClose={handleAuthClose}
                  >
                    <MenuItem
                      onClick={() => {
                        handleAuthClose();
                        window.open("/dashboard");
                      }}
                    >
                      {en.common.dashboard}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleAuthClose();
                        logout({ logoutParams: { returnTo: window.location.origin } });
                      }}
                    >
                      {en.common.logout}
                    </MenuItem>
                  </Menu>
                </>
              )}

              {!isAuthenticated && (
                <Link
                  variant="h6"
                  component="button"
                  onClick={() => loginWithRedirect()}
                  underline="none"
                  sx={{
                    ...rightLink,
                    color: "secondary.light",
                    whiteSpace: "nowrap",
                  }}
                >
                  {en.common.login}
                </Link>
              )}
            </Box>
          )}

          {/* Mobile Hamburger Menu */}
          {isMobile && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuOpen}
                sx={{ color: "secondary.light" }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="mobile-menu"
                anchorEl={mobileMenuAnchorEl}
                keepMounted
                open={Boolean(mobileMenuAnchorEl)}
                onClose={handleMobileMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/about");
                  }}
                >
                  {en.marketing.about}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.location.href = "/features#essaycraft";
                  }}
                >
                  essaycraft ai
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.location.href = "/features#inkbowl-publishing";
                  }}
                >
                  inkbowl publishing
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.location.href = "/features#inklink";
                  }}
                >
                  inklink network
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.location.href = "/features#versecanvas";
                  }}
                >
                  versecanvas ai
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/digital-archive");
                  }}
                >
                  {en.marketing.publicationArchive}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/publishing");
                  }}
                >
                  {en.common.publishing} — {en.marketing.about}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/submissions");
                  }}
                >
                  submissions
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/community");
                  }}
                >
                  {en.marketing.inkLink}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/guidelines");
                  }}
                >
                  {en.marketing.communityGuideline}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/publications");
                  }}
                >
                  {en.common.publications}
                </MenuItem>
                {isAuthenticated && (
                  <MenuItem
                    onClick={() => {
                      handleMobileMenuClose();
                      window.open("/dashboard");
                    }}
                  >
                    {en.common.dashboard}
                  </MenuItem>
                )}
                {isAuthenticated && isAdmin && (
                  <MenuItem
                    onClick={() => {
                      handleMobileMenuClose();
                      window.open("/admin");
                    }}
                  >
                    Admin
                  </MenuItem>
                )}
                {isAuthenticated && (
                  <MenuItem
                    onClick={() => {
                      handleMobileMenuClose();
                      logout({
                        logoutParams: { returnTo: window.location.origin },
                      });
                    }}
                  >
                    {en.common.logout}
                  </MenuItem>
                )}
                {!isAuthenticated && (
                  <MenuItem
                    onClick={() => {
                      handleMobileMenuClose();
                      loginWithRedirect();
                    }}
                  >
                    {en.common.login}
                  </MenuItem>
                )}
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />
    </React.Fragment>
  );
}

export default AppAppBar;
